import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import useManualInput from './useManualInput';
import { Box, Field, Input, Select, Row, Button, List, Note } from './manualInputStyles';

/**
 * Obstacles and opportunities by hand (#687): add items one at a time to a pivot
 * point through one of the six lenses. Editing and removing happen in the
 * Esteet ja mahdollisuudet view as usual.
 */
export default function LensItemsAdmin({ instanceId, locked = false, onApplied }) {
  const { t } = useTranslation();
  const { context, busy, error, run } = useManualInput(instanceId, 'lens_items_bootstrap', { onApplied });
  const [form, setForm] = useState({ pivotId: '', lensName: '', type: 'obstacle', title: '' });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (!context) return <Box>{error ? <Note $error>{error}</Note> : t('manualInputs.loading')}</Box>;
  const { lenses, pivots, items } = context;
  const pivotId = form.pivotId || pivots[0]?.id || '';
  const lensName = form.lensName || lenses[0]?.name || '';
  const nameOf = (list, id, key = 'title') => list.find((x) => x.id === id)?.[key] || '';

  const add = async () => {
    const data = await run('apply', { items: [{ pivot_point_id: pivotId, lensName, type: form.type, title: form.title.trim() }] });
    if (data) setForm((f) => ({ ...f, title: '' }));
  };

  return (
    <Box>
      <h3>{t('manualInputs.lensItems.title')}</h3>
      <p className="intro">{t('manualInputs.lensItems.intro')}</p>
      {!pivots.length ? (
        <Note $warn>{t('manualInputs.lensItems.noPivots')}</Note>
      ) : (
        <>
          <Row>
            <Field>{t('manualInputs.lensItems.pivot')}
              <Select value={pivotId} onChange={set('pivotId')} disabled={busy || locked}>
                {pivots.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </Select>
            </Field>
            <Field>{t('manualInputs.lensItems.lens')}
              <Select value={lensName} onChange={set('lensName')} disabled={busy || locked}>
                {lenses.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
              </Select>
            </Field>
            <Field>{t('manualInputs.lensItems.type')}
              <Select value={form.type} onChange={set('type')} disabled={busy || locked}>
                <option value="obstacle">{t('manualInputs.lensItems.obstacle')}</option>
                <option value="opportunity">{t('manualInputs.lensItems.opportunity')}</option>
              </Select>
            </Field>
          </Row>
          <Row>
            <Field style={{ flexBasis: '100%' }}>{t('manualInputs.lensItems.itemTitle')}
              <Input value={form.title} onChange={set('title')} disabled={busy || locked}
                onKeyDown={(e) => { if (e.key === 'Enter' && form.title.trim()) add(); }} />
            </Field>
            <Button type="button" $primary onClick={add} disabled={busy || locked || !form.title.trim()}>
              <Plus size={16} /> {t('manualInputs.lensItems.add')}
            </Button>
          </Row>
        </>
      )}
      <p className="intro" style={{ marginTop: 16 }}>{t('manualInputs.lensItems.count', { count: items.length })}</p>
      <List>
        {items.slice(-10).reverse().map((i) => (
          <li key={i.id}>
            {i.title}
            <span className="meta">
              {i.type === 'opportunity' ? t('manualInputs.lensItems.opportunity') : t('manualInputs.lensItems.obstacle')}
              {' · '}{nameOf(lenses, i.lens_id, 'name')}{' · '}{nameOf(pivots, i.pivot_point_id)}
            </span>
          </li>
        ))}
      </List>
      {error && <Note $error>{error}</Note>}
      {locked && <Note $warn>{t('manualInputs.locked')}</Note>}
    </Box>
  );
}
